import React from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { Form as FinalForm } from 'react-final-form';
import { Form } from 'semantic-ui-react';
import isEqual from 'lodash.isequal';
import moment from 'moment';

// Types
// TODO
// import { AppState } from 'services/types';

// Selectors
// @ts-ignore
import { selectInitialValues } from 'services/timesheetTimesheetEntriesForm/selectors';

// Utils
// @ts-ignore
import { formatEntryForForm } from 'utils/timesheets';

// Form
// @ts-ignore
import { validators } from 'forms';
import { timesheetEntriesDecorator } from 'forms/decorators';

// Global Components
// @ts-ignore
import CurrentSeasonInProgressGate from 'components/CurrentSeasonInProgressGate';
import DisableUserSelect from 'components/DisableUserSelect';
import PageTitleAndFiltersPusher from 'components/PageTitleAndFiltersPusher';
import SelectedTimesheetIsUnapprovedGate from 'components/SelectedTimesheetIsUnapprovedGate';
import RoleGate from 'components/RoleGate';
import {
    ADMIN,
    MANAGER,
    ASSISTANT_MANAGER
} from 'components/RoleGate/constants';

// Child Components
// @ts-ignore
import TimesheetTimesheetEntryModal from '../../../../components/TimesheetTimesheetEntryModal';
// @ts-ignore
import DeleteTimesheetTimesheetEntriesModal from '../../../../components/DeleteTimesheetTimesheetEntriesModal';
// @ts-ignore
import TimesheetTimesheetEntriesAPIErrorModal from '../TimesheetTimesheetEntriesAPIErrorModal';
// @ts-ignore
import TimesheetEntrySpitModal from './components/TimesheetEntrySpitModal';
// @ts-ignore
import TimesheetTimesheetEntriesFormAutoSave from './components/TimesheetTimesheetEntriesFormAutoSave';
// @ts-ignore
import FormApplyUnsavedValues from './components/FormApplyUnsavedValues';
// @ts-ignore
import FormBulkChange from './components/FormBulkChange';
// @ts-ignore
import FormStateToRedux from './components/FormStateToRedux';
// @ts-ignore
import TimesheetTimesheetEntriesTable from './components/TimesheetTimesheetEntriesTable';
import TimesheetImportTimesheetEntryModal from '../../../../components/TimesheetImportTimesheetEntryModal';

// Validators
const {
    composeValidators,
    isRequired,
    isNotIncompleteDecimal,
    isInteger,
    isPositiveNumber,
    maxValue60,
    maxValue1000
} = validators;

interface InitialValues {
    [uuid: string]: {};
}

interface OwnProps {
    timesheetId: number;
}

interface ConnectedProps {
    initialValues?: InitialValues;
}

type Props = OwnProps & ConnectedProps;

export class TimesheetTimesheetEntriesForm extends React.PureComponent<Props> {
    handleSubmit = (): void => {};

    render(): React.ReactElement {
        const { initialValues, timesheetId } = this.props;

        return (
            <FinalForm
                onSubmit={this.handleSubmit}
                decorators={[timesheetEntriesDecorator]}
                initialValues={initialValues}
                mutators={{
                    insertRows: (args, state, tools): void => {
                        const values = args[0];
                        if (values && values.length) {
                            values.forEach((value: any): void => {
                                const formattedValue =
                                    formatEntryForForm(value);
                                tools.changeValue(
                                    state,
                                    formattedValue.uuid,
                                    (): any => formattedValue
                                );
                            });
                        }
                    },
                    deleteRows: (args, state, tools): void => {
                        const values = args[0];

                        if (values && values.length) {
                            values.forEach((value: string): void => {
                                tools.changeValue(
                                    state,
                                    value,
                                    (): undefined => undefined
                                );
                            });
                        }
                    },
                    revertRows: (args, state: any, tools): void => {
                        const { formState } = state;
                        const uuids = args[0];

                        if (uuids && uuids.length) {
                            uuids.forEach((uuid: string): void => {
                                tools.changeValue(
                                    state,
                                    uuid,
                                    (): any => formState.initialValues[uuid]
                                );
                            });
                        }
                    }
                }}
                subscription={{
                    initialValues: true,
                    values: true,
                    errors: true,
                    active: true,
                    valid: true,
                    dirty: true
                }}
                initialValuesEqual={(
                    initialValues1: any,
                    initialValues2: any
                ): boolean => {
                    return isEqual(initialValues1, initialValues2);
                }}
                validate={(values: any): any => {
                    const allErrors: any = {};
                    const keys = Object.keys(values);

                    keys.forEach((key: string): void => {
                        const {
                            date,
                            startTime,
                            endTime,
                            unpaidBreakMinutes,
                            workTypeCompositeKey,
                            entityCompositeKey,
                            paidBreakMinutes,
                            units,
                            rate,
                            lastReviewIsApproved
                        } = values[key];

                        if (!lastReviewIsApproved) {
                            const [, isPieceRateString] = (
                                workTypeCompositeKey || ''
                            ).split('.');
                            const isPieceRate = isPieceRateString === 'true';

                            const dateError = isRequired(date);
                            const startTimeError = isRequired(startTime);
                            let endTimeError = isRequired(endTime);
                            const workTypeCompositeKeyError =
                                isRequired(workTypeCompositeKey);
                            const entityCompositeKeyError =
                                isRequired(entityCompositeKey);
                            let unitsError;
                            let rateError;
                            let paidBreakMinutesError;

                            unitsError = composeValidators(
                                isNotIncompleteDecimal,
                                isPositiveNumber,
                                maxValue1000
                            )(units);

                            if (isPieceRate) {
                                unitsError = composeValidators(
                                    isRequired,
                                    isNotIncompleteDecimal,
                                    isPositiveNumber,
                                    maxValue1000
                                )(units);
                                rateError = composeValidators(
                                    isRequired,
                                    isNotIncompleteDecimal,
                                    isPositiveNumber,
                                    maxValue1000
                                )(rate);
                                paidBreakMinutesError = composeValidators(
                                    isPositiveNumber,
                                    isNotIncompleteDecimal,
                                    isInteger,
                                    maxValue60
                                )(paidBreakMinutes);
                            }

                            if (startTime && endTime) {
                                const momentStartTime = moment(
                                    startTime,
                                    'HH:mm'
                                );
                                const momentEndTime = moment(endTime, 'HH:mm');
                                const unpaidBreakMinutesNumber =
                                    unpaidBreakMinutes || 0;
                                const paidBreakMinutesNumber =
                                    paidBreakMinutes || 0;

                                if (
                                    momentEndTime.diff(
                                        momentStartTime,
                                        'minutes'
                                    ) -
                                        unpaidBreakMinutesNumber -
                                        paidBreakMinutesNumber <=
                                    0
                                ) {
                                    endTimeError =
                                        'End time must be more than the total breaks after the start time';
                                }

                                if (
                                    momentEndTime.isSameOrBefore(
                                        momentStartTime
                                    )
                                ) {
                                    endTimeError =
                                        'End time must be after the start time';
                                }
                            }

                            const errors = {
                                ...(dateError ? { date: dateError } : {}),
                                ...(startTimeError
                                    ? { startTime: startTimeError }
                                    : {}),
                                ...(startTimeError
                                    ? { startTime: startTimeError }
                                    : {}),
                                ...(endTimeError
                                    ? { endTime: endTimeError }
                                    : {}),
                                ...(workTypeCompositeKeyError
                                    ? {
                                          workTypeCompositeKey:
                                              workTypeCompositeKeyError
                                      }
                                    : {}),
                                ...(entityCompositeKeyError
                                    ? {
                                          entityCompositeKey:
                                              entityCompositeKeyError
                                      }
                                    : {}),
                                ...(unitsError ? { units: unitsError } : {}),
                                ...(rateError ? { rate: rateError } : {}),
                                ...(paidBreakMinutesError
                                    ? {
                                          paidBreakMinutes:
                                              paidBreakMinutesError
                                      }
                                    : {})
                            };

                            if (Object.keys(errors).length) {
                                allErrors[key] = errors;
                            }
                        }
                    });
                    return allErrors;
                }}
                render={(formProps): React.ReactElement => {
                    const {
                        form: {
                            batch,
                            change,
                            mutators: { insertRows, deleteRows, revertRows }
                        }
                    } = formProps;
                    return (
                        <React.Fragment>
                            <TimesheetTimesheetEntryModal
                                // TODO: remove the use of any
                                onSubmit={(values: any): void => {
                                    insertRows(values);
                                }}
                            />
                            <TimesheetImportTimesheetEntryModal
                                onSubmit={(values: any): void => {
                                    insertRows(values);
                                }}
                            />
                            <TimesheetTimesheetEntriesAPIErrorModal
                                revertRows={revertRows}
                            />
                            <RoleGate
                                requiredRoles={[
                                    ADMIN,
                                    MANAGER,
                                    ASSISTANT_MANAGER
                                ]}
                            >
                                <CurrentSeasonInProgressGate>
                                    <TimesheetEntrySpitModal
                                        insertRows={insertRows}
                                    />
                                    <DeleteTimesheetTimesheetEntriesModal
                                        // TODO: remove the use of any
                                        onSubmit={(values: any): void => {
                                            deleteRows(values);
                                        }}
                                    />
                                </CurrentSeasonInProgressGate>
                            </RoleGate>
                            <PageTitleAndFiltersPusher compact>
                                <Form autoComplete="off">
                                    <TimesheetTimesheetEntriesFormAutoSave
                                        namespace={`TimesheetTimesheetEntriesForm.${timesheetId}`}
                                        formProps={formProps}
                                    />
                                    <SelectedTimesheetIsUnapprovedGate>
                                        <FormApplyUnsavedValues
                                            namespace={`TimesheetTimesheetEntriesForm.${timesheetId}`}
                                            batch={batch}
                                            change={change}
                                        />
                                    </SelectedTimesheetIsUnapprovedGate>
                                    <FormBulkChange
                                        batch={batch}
                                        change={change}
                                        formProps={formProps}
                                        format={formatEntryForForm}
                                    />
                                    <FormStateToRedux
                                        namespace={`TimesheetTimesheetEntriesForm.${timesheetId}`}
                                        formProps={formProps}
                                    />
                                    <DisableUserSelect>
                                        <TimesheetTimesheetEntriesTable />
                                    </DisableUserSelect>
                                </Form>
                            </PageTitleAndFiltersPusher>
                        </React.Fragment>
                    );
                }}
            />
        );
    }
}

const mapStateToProps = createStructuredSelector<
    any, // AppState,
    OwnProps,
    ConnectedProps
>({
    initialValues: selectInitialValues
});

export default connect(mapStateToProps)(TimesheetTimesheetEntriesForm);
